"use client";

import { useLingui } from "@lingui/react/macro";
import { Badge } from "@offstreetparking/design-system/dataDisplay/Badge";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { radioCardStyles } from "@offstreetparking/design-system/forms";
import { ChevronRightIcon } from "@offstreetparking/design-system/icons/ChevronRightIcon";
import { CircleAlertIcon } from "@offstreetparking/design-system/icons/CircleAlertIcon";
import { ParkingIcon } from "@offstreetparking/design-system/icons/ParkingIcon";
import { PermitTypeIcon } from "@offstreetparking/design-system/icons/PermitTypeIcon";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import type { RouterOutputs } from "../../api/types";
import { routes } from "../../routes/routes";
import type { RegSettingOption } from "../../types/entry-points";
import { OverrideNoticeAlert } from "../OverrideNoticeAlert/OverrideNoticeAlert";
import { CapacityFullBadge } from "../dataDisplay/CapacityFullBadge";

type LocationLookupResult = NonNullable<RouterOutputs["locationLookup"]["getLocationLookupEntitiesByIdentifier"]>;

type CombinedParkingOptionSelectionProps = {
	permitTypes: LocationLookupResult["permitTypes"];
	regSettings: RegSettingOption[];
	overrideNotices?: LocationLookupResult["overrideNotices"];
	timezone?: string;
	navOutRef?: string;
	onSelectPermitType: (permitTypeId: number) => void;
	onSelectRegSetting: (settingId: number) => void;
};
export const CombinedParkingOptionSelection = ({
	permitTypes = [],
	regSettings = [],
	overrideNotices,
	timezone,
	navOutRef,
	onSelectPermitType,
	onSelectRegSetting,
}: CombinedParkingOptionSelectionProps) => {
	const firstButtonRef = useRef<HTMLButtonElement>(null);
	const sectionRef = useRef<HTMLElement>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-trigger focus when options arrive
	useEffect(() => {
		if (!firstButtonRef.current) {
			return;
		}
		if (typeof document === "undefined") {
			return;
		}
		const active = document.activeElement;
		const isInsideSection = sectionRef.current?.contains(active);
		if (!isInsideSection) {
			firstButtonRef.current.focus();
		}
	}, [permitTypes, regSettings]);
	const { t } = useLingui();
	const router = useRouter();
	const overridePermitTypes = permitTypes.filter((permitType) => permitType.isOverride);
	const regularPermitTypes = permitTypes.filter((permitType) => !permitType.isOverride);
	// Intentional: when every affected permit type is fully hidden there is nothing visible to
	// explain, so the notice is suppressed rather than pointing at rows that are not there.
	const hasVisiblyAffectedPermitType = permitTypes.some((permitType) => permitType.override !== undefined);
	const notices = overrideNotices ?? [];
	const listedPermitTypeIds = new Set(permitTypes.map((permitType) => permitType.id));
	const bannerNotices = notices.filter((notice) => !listedPermitTypeIds.has(notice.overridingPermitTypeId));
	const listNotices = notices.filter((notice) => listedPermitTypeIds.has(notice.overridingPermitTypeId));
	const showOverrideNotice = hasVisiblyAffectedPermitType && listNotices.length > 0;
	let isFirstButton = true;
	const renderPermitType = (permitType: NonNullable<LocationLookupResult["permitTypes"]>[number]) => {
		const isFull = permitType.status === "full";
		const isLimited = permitType.override?.state === "limited";
		const registrationOpensAt = permitType.registrationOpensAt ? new Date(permitType.registrationOpensAt) : null;
		const opensWithinWeek =
			registrationOpensAt !== null && registrationOpensAt.getTime() - Date.now() < 6 * 24 * 60 * 60 * 1000;
		const isDisabled = isFull || permitType.override?.state === "disabled" || registrationOpensAt !== null;
		const permitTypeDisabledClassName = isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer";
		// Disabled buttons can't receive focus, so the ref anchors on the first enabled one.
		const ref = isFirstButton && !isDisabled ? firstButtonRef : undefined;
		isFirstButton = isFirstButton && isDisabled;
		return (
			<li key={`pt-${permitType.id}`}>
				<button
					ref={ref}
					aria-label={`${t`Select a Permit Type`}: ${permitType.title}`}
					onClick={() => {
						if (isDisabled) {
							return;
						}
						if (permitType.overrideNavOutLookupId) {
							router.push(
								routes.locationLookup({ lookup: permitType.overrideNavOutLookupId, ref: navOutRef }),
							);
							return;
						}
						onSelectPermitType(permitType.id);
					}}
					type="button"
					disabled={isDisabled}
					className={classNames(
						radioCardStyles({
							checked: false,
						}),
						"flex w-full flex-row items-center px-4 py-3 hover:border-primary",
						permitTypeDisabledClassName,
					)}
				>
					<div className="flex flex-1 flex-col gap-1 text-left">
						<Text As="span" weight="bold">
							{permitType.title}
						</Text>
						{permitType.description && (
							<Text As="span" size="sm" intent="muted">
								{permitType.description}
							</Text>
						)}
						{isFull && (
							<div className="w-fit">
								<CapacityFullBadge />
							</div>
						)}
						{registrationOpensAt && (
							<div className="w-fit">
								<Badge variant="success" size="sm" icon={ParkingIcon}>
									{(() => {
										const dayStr = formatDate({
											date: registrationOpensAt,
											formatType: opensWithinWeek ? "Weekday" : "Abbreviated_Date",
											timezone,
										});
										const timeStr = formatDate({
											date: registrationOpensAt,
											formatType: "Time",
											timezone,
										});
										return t`Registration starts ${dayStr} at ${timeStr}`;
									})()}
								</Badge>
							</div>
						)}
						{isLimited && (
							<div className="w-fit">
								<Badge variant="warning" size="sm" icon={CircleAlertIcon}>
									{t({
										comment:
											"Badge on a permit type card when an active permit type override disables some (not all) of its parking options",
										message: "Limited options available",
									})}
								</Badge>
							</div>
						)}
					</div>
					{!isDisabled && <ChevronRightIcon className="h-6 w-6 text-primary" aria-hidden="true" />}
				</button>
			</li>
		);
	};
	return (
		<section ref={sectionRef} aria-labelledby="parking-option-heading" className="space-y-4">
			<Heading id="parking-option-heading" className="mt-0.5 flex items-center gap-2">
				<PermitTypeIcon className="h-5 w-5" />
				{t`Select a Permit Type`}
			</Heading>

			{bannerNotices.length > 0 && <OverrideNoticeAlert notices={bannerNotices} />}

			{permitTypes.length === 0 && regSettings.length === 0 && (
				<Text size="sm" intent="muted">
					{t`There are no parking options available at this time.`}
				</Text>
			)}

			<ul className="flex flex-col gap-2">
				{overridePermitTypes.map(renderPermitType)}

				{showOverrideNotice && (
					<li>
						<div className="mt-2 rounded-md bg-neutral-faded p-3">
							<Text size="sm" intent="muted">
								{listNotices.length === 1 && listNotices[0]
									? t`Options below may be unavailable or limited due to ${listNotices[0].overridingPermitTypeTitle}`
									: t`Options below may be unavailable or limited due to active permit type overrides`}
							</Text>
						</div>
					</li>
				)}

				{regularPermitTypes.map(renderPermitType)}

				{regSettings.map((setting) => {
					const isDisabled = !setting.isAvailable || setting.isAtCapacity;
					const regSettingDisabledClassName = isDisabled ? "cursor-not-allowed opacity-50" : "";
					const ref = isFirstButton && !isDisabled ? firstButtonRef : undefined;
					isFirstButton = isFirstButton && isDisabled;
					return (
						<li key={`rs-${setting.id}`}>
							<button
								ref={ref}
								data-testid={setting.id}
								aria-label={`${t`Select a Permit Type`}: ${setting.name}`}
								onClick={() => !isDisabled && onSelectRegSetting(setting.id)}
								type="button"
								disabled={isDisabled}
								className={classNames(
									radioCardStyles({
										checked: false,
									}),
									"flex w-full cursor-pointer flex-row items-center px-4 py-3 hover:border-primary",
									regSettingDisabledClassName,
								)}
							>
								<div className="flex flex-1 flex-col gap-1 text-left">
									<Text As="span" weight="bold">
										{setting.name}
									</Text>
									{setting.description && (
										<Text As="span" size="sm" intent="muted">
											{setting.description}
										</Text>
									)}
									{setting.isAtCapacity && (
										<div className="w-fit">
											<CapacityFullBadge />
										</div>
									)}
								</div>
								{!isDisabled && (
									<ChevronRightIcon className="h-6 w-6 text-primary" aria-hidden="true" />
								)}
							</button>
						</li>
					);
				})}
			</ul>
		</section>
	);
};
